import { DateTime } from 'luxon';
import React, { useCallback } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { ActionLog } from '@/types/logs';

import InfoRow from './infoRow';
import styles from './logstyles.module.scss';
type Props = {
  selectedLog: ActionLog;
};



const ActionLogDetailView = ( { selectedLog }: Props ) => {
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const formatTimestamp = useCallback( ( timestamp: string ) => {
    if ( !timestamp ) return 'N/A';

    // Parse ISO string as UTC
    const dt = DateTime.fromISO( timestamp, { zone: 'utc' } );

    // Convert to UTC or local based on preference
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();

    // Format: "Feb 12, 2026, 5:17:20.383 PM (PST)"
    return zonedDt.toFormat( 'LLL d, yyyy, h:mm:ss.SSS (ZZZZ)' );
  }, [ useLocalTimeZone ] );



  return (
    <div>
      <div>

        {/* Header */}
        <div className={ styles.card }>
          <div className={ styles.header }>
            <Heading as="h2" size="md" className={ styles.title }>Action Log Details</Heading>
            <span className={ styles.statusBadge }>Completed</span>
          </div>
          <InfoRow label="Log ID" value={ selectedLog.id } />
          <InfoRow label="Timestamp" value={ formatTimestamp( selectedLog.start_time ) } />
        </div>

        {/* Request Details */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Request Details</Heading>
          <InfoRow label="Client ID" value={ selectedLog.client_id } mono />
          {selectedLog.agent_id && <InfoRow label="Agent ID" value={ selectedLog.agent_id } mono />}
          <InfoRow label="Request ID" value={ selectedLog.request_id } mono />
          <InfoRow label="Correlation ID" value={ selectedLog.correlation_id } mono />
        </div>
        {selectedLog.string_input && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Action Input</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {( () => {
                  try {
                    return JSON.stringify( JSON.parse( selectedLog.string_input ), null, 2 );
                  } catch {
                    return selectedLog.string_input;
                  }
                } )()}
              </pre>
            </div>
          </div>
        )}

        {selectedLog.meta_data && (
          <div className={ styles.card }>
            <Heading as="h2" size="md" className={ styles.sectionTitle }>Metadata</Heading>
            <div className={ styles.jsonContainer }>
              <pre className={ styles.jsonContent }>
                {JSON.stringify( selectedLog.meta_data, null, 2 )}
              </pre>
            </div>
          </div>
        )}

        {/* Package Info */}
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Package Information</Heading>
          <InfoRow label="Constellation Name" value={ selectedLog.constellation_name } />
          <InfoRow label="Constellation Version" value={ selectedLog.constellation_version } />
          <InfoRow label="Component Name" value={ selectedLog.component_name } />
        </div>

      </div>
    </div>
  );
};

export default ActionLogDetailView;